import React, { useState, useEffect } from 'react';
import { Calendar, Clock, AlertTriangle, CheckCircle, TrendingUp, Filter, Zap } from 'lucide-react';
import { API_URL } from '../../services/api';

function HistoriqueTaches() {
  const [taches, setTaches] = useState([]);
  const [periode, setPeriode] = useState('tout');
  const [loading, setLoading] = useState(false);
  const [filtreStatut, setFiltreStatut] = useState('en_cours'); // en_cours | terminee | tout
  const [filtreAlerte, setFiltreAlerte] = useState('tout');     // tout | urgent | retard | lent

  useEffect(() => { loadTaches(); }, [periode]);

  const loadTaches = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/atelier/historique-taches?periode=${periode}`);
      if (response.ok) {
        const data = await response.json();
        setTaches(Array.isArray(data) ? data : []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Recharger quand periode change
  const changePeriode = async (p) => {
    setPeriode(p);
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/atelier/historique-taches?periode=${p}`);
      if (response.ok) { const data = await response.json(); setTaches(Array.isArray(data) ? data : []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const formatDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const getLivraisonInfo = (jours) => {
    if (jours === null || jours === undefined) return null;
    const j = parseInt(jours);
    if (j < 0)  return { label: `En retard de ${Math.abs(j)}j`, cls: 'text-red-700 font-bold bg-red-50 px-2 py-0.5 rounded' };
    if (j === 0) return { label: 'Livraison aujourd\'hui !', cls: 'text-red-700 font-bold bg-red-100 px-2 py-0.5 rounded animate-pulse' };
    if (j <= 2)  return { label: `${j}j restant${j > 1 ? 's' : ''}`, cls: 'text-orange-700 font-bold bg-orange-50 px-2 py-0.5 rounded' };
    if (j <= 7)  return { label: `${j}j restants`, cls: 'text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded' };
    return { label: `${j}j restants`, cls: 'text-gray-500 text-xs' };
  };

  const getRowBg = (tache) => {
    if (tache.alerte === 'retard') return 'bg-red-50 border-l-4 border-red-500';
    if (tache.alerte === 'urgent') return 'bg-orange-50 border-l-4 border-orange-400';
    if (tache.alerte === 'lent')   return 'bg-yellow-50 border-l-4 border-yellow-400';
    if (tache.statut === 'termine') return 'bg-gray-50';
    return 'bg-white';
  };

  const pct = (t) => {
    const tot = parseInt(t.total_etapes || 0);
    const done = parseInt(t.etapes_terminees || 0);
    return tot > 0 ? Math.round((done / tot) * 100) : 0;
  };

  // Filtrage
  const tachesFiltrees = taches.filter(t => {
    const estTerminee = t.statut === 'termine';
    if (filtreStatut === 'en_cours' && estTerminee) return false;
    if (filtreStatut === 'terminee' && !estTerminee) return false;
    if (filtreAlerte !== 'tout' && t.alerte !== filtreAlerte) return false;
    return true;
  });

  const nbEnCours = taches.filter(t => t.statut !== 'termine').length;
  const nbRetard = taches.filter(t => t.alerte === 'retard').length;
  const nbUrgent = taches.filter(t => t.alerte === 'urgent').length;
  const nbLent = taches.filter(t => t.alerte === 'lent').length;

  const btnPeriode = (val, label) => (
    <button onClick={() => changePeriode(val)}
      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
        periode === val ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
      }`}>{label}</button>
  );

  const btnFiltre = (val, label, active, setter, color) => (
    <button onClick={() => setter(active === val ? (val === 'tout' ? 'tout' : 'tout') : val)}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
        active === val ? `${color} text-white border-transparent` : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
      }`}>{label}</button>
  );

  return (
    <div className="p-4 md:p-6 max-w-full">
      <div className="mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TrendingUp className="text-blue-600" />
          Suivi de Production
        </h1>
        <p className="text-gray-500 text-sm mt-1">Tâches en cours, avancement des OFs, alertes délais</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500 cursor-pointer" onClick={() => setFiltreStatut('en_cours')}>
          <div className="text-gray-500 text-xs uppercase tracking-wide">En cours</div>
          <div className="text-2xl font-bold text-blue-600">{nbEnCours}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-500 cursor-pointer" onClick={() => { setFiltreStatut('tout'); setFiltreAlerte('retard'); }}>
          <div className="text-gray-500 text-xs uppercase tracking-wide">En retard</div>
          <div className="text-2xl font-bold text-red-600">{nbRetard}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-orange-400 cursor-pointer" onClick={() => { setFiltreStatut('tout'); setFiltreAlerte('urgent'); }}>
          <div className="text-gray-500 text-xs uppercase tracking-wide flex items-center gap-1"><Zap className="h-3 w-3" /> Urgentes</div>
          <div className="text-2xl font-bold text-orange-600">{nbUrgent}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-yellow-400 cursor-pointer" onClick={() => { setFiltreStatut('tout'); setFiltreAlerte('lent'); }}>
          <div className="text-gray-500 text-xs uppercase tracking-wide">Lentes</div>
          <div className="text-2xl font-bold text-yellow-600">{nbLent}</div>
        </div>
      </div>

      {/* Barre de filtres */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4 flex flex-wrap gap-4 items-center">
        {/* Période */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-gray-400" />
          {btnPeriode('semaine', 'Cette semaine')}
          {btnPeriode('mois', 'Ce mois')}
          {btnPeriode('annee', 'Cette année')}
          {btnPeriode('tout', 'Tout')}
        </div>

        <div className="w-px h-8 bg-gray-200 hidden md:block" />

        {/* Statut */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 font-medium uppercase">Afficher :</span>
          <button onClick={() => setFiltreStatut('en_cours')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filtreStatut === 'en_cours' ? 'bg-blue-600 text-white border-transparent' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
            ▶ En cours
          </button>
          <button onClick={() => setFiltreStatut('terminee')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filtreStatut === 'terminee' ? 'bg-green-600 text-white border-transparent' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
            ✓ Terminées
          </button>
          <button onClick={() => setFiltreStatut('tout')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filtreStatut === 'tout' ? 'bg-gray-700 text-white border-transparent' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
            Toutes
          </button>
        </div>

        <div className="w-px h-8 bg-gray-200 hidden md:block" />

        {/* Alerte */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 font-medium uppercase">Alerte :</span>
          <button onClick={() => setFiltreAlerte('tout')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filtreAlerte === 'tout' ? 'bg-gray-700 text-white border-transparent' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
            Tout
          </button>
          <button onClick={() => setFiltreAlerte('retard')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filtreAlerte === 'retard' ? 'bg-red-600 text-white border-transparent' : 'bg-white text-red-600 border-red-200 hover:bg-red-50'}`}>
            🔴 Retard
          </button>
          <button onClick={() => setFiltreAlerte('urgent')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filtreAlerte === 'urgent' ? 'bg-orange-500 text-white border-transparent' : 'bg-white text-orange-600 border-orange-200 hover:bg-orange-50'}`}>
            🟠 Urgent
          </button>
          <button onClick={() => setFiltreAlerte('lent')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filtreAlerte === 'lent' ? 'bg-yellow-500 text-white border-transparent' : 'bg-white text-yellow-600 border-yellow-200 hover:bg-yellow-50'}`}>
            🟡 Lent
          </button>
        </div>
      </div>

      {/* Résultat */}
      <div className="text-xs text-gray-400 mb-2">{tachesFiltrees.length} tâche{tachesFiltrees.length !== 1 ? 's' : ''} affichée{tachesFiltrees.length !== 1 ? 's' : ''}</div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <p className="mt-3 text-gray-500">Chargement...</p>
        </div>
      ) : tachesFiltrees.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-400">
          <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Aucune tâche{filtreStatut === 'en_cours' ? ' en cours' : ''} pour ces filtres</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Alerte</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">OF / Livraison</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Bijou</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Avancement OF</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Étape</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Ouvrier</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Début / Fin</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Or</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tachesFiltrees.map((tache) => {
                  const livrInfo = getLivraisonInfo(tache.jours_avant_livraison);
                  const avancement = pct(tache);
                  return (
                    <tr key={tache.affectation_id} className={`transition-colors hover:brightness-95 ${getRowBg(tache)}`}>
                      {/* Alerte */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {tache.alerte === 'retard' && (
                          <span className="flex items-center gap-1 text-xs font-bold text-red-700">
                            <AlertTriangle className="h-4 w-4" /> Retard
                          </span>
                        )}
                        {tache.alerte === 'urgent' && (
                          <span className="flex items-center gap-1 text-xs font-bold text-orange-600">
                            <Zap className="h-4 w-4" /> Urgent
                          </span>
                        )}
                        {tache.alerte === 'lent' && (
                          <span className="flex items-center gap-1 text-xs text-yellow-600">
                            <Clock className="h-4 w-4" /> Lent
                          </span>
                        )}
                        {(!tache.alerte || tache.alerte === 'normal') && (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        )}
                      </td>

                      {/* OF + livraison */}
                      <td className="px-3 py-3">
                        <div className="font-semibold text-blue-600">{tache.numero_ordre}</div>
                        {tache.date_livraison_prevue ? (
                          <div className="mt-0.5">
                            <div className="text-xs text-gray-400">Livraison : {formatDate(tache.date_livraison_prevue)}</div>
                            {livrInfo && <div className={`mt-0.5 text-xs ${livrInfo.cls}`}>{livrInfo.label}</div>}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">Pas de date</div>
                        )}
                      </td>

                      {/* Bijou */}
                      <td className="px-3 py-3">
                        <div className="font-medium">{tache.type_bijou}</div>
                        <div className="text-xs text-gray-400 truncate max-w-[160px]">{tache.description}</div>
                      </td>

                      {/* Avancement OF */}
                      <td className="px-3 py-3 min-w-[120px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                avancement === 100 ? 'bg-green-500' :
                                avancement >= 70 ? 'bg-blue-500' :
                                avancement >= 40 ? 'bg-yellow-400' : 'bg-orange-400'
                              }`}
                              style={{ width: `${avancement}%` }}
                            />
                          </div>
                          <span className="text-xs font-bold text-gray-600 w-8">{avancement}%</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {tache.etapes_terminees}/{tache.total_etapes} étapes
                        </div>
                      </td>

                      {/* Étape */}
                      <td className="px-3 py-3">
                        <span className="font-medium text-purple-600">{tache.mission}</span>
                      </td>

                      {/* Ouvrier */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {tache.ouvrier_prenom} {tache.ouvrier_nom}
                      </td>

                      {/* Statut */}
                      <td className="px-3 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          tache.statut === 'termine' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {tache.statut === 'termine' ? '✓ Terminée' : '▶ En cours'}
                        </span>
                      </td>

                      {/* Dates */}
                      <td className="px-3 py-3 text-xs text-gray-500">
                        <div>▶ {formatDateTime(tache.date_debut)}</div>
                        {tache.date_fin ? (
                          <div className="text-green-600">■ {formatDateTime(tache.date_fin)}</div>
                        ) : (
                          <div className={`font-semibold ${
                            tache.duree_heures > 48 ? 'text-red-600' :
                            tache.duree_heures > 24 ? 'text-orange-500' :
                            tache.duree_heures > 8  ? 'text-yellow-600' : 'text-gray-400'
                          }`}>
                            ⏳ {tache.duree_heures}h en cours
                          </div>
                        )}
                      </td>

                      {/* Or */}
                      <td className="px-3 py-3 text-xs">
                        {(() => {
                          const donne = parseFloat(tache.poids_or_donne) || 0;
                          const utilise = parseFloat(tache.poids_bijou_fini) || 0;
                          const restant = parseFloat(tache.or_restant) || 0;
                          const anomalie = donne === 0 && utilise > 0; // or utilisé sans avoir été donné
                          return (
                            <>
                              {anomalie ? (
                                <div className="text-red-600 font-semibold">
                                  ⚠ Donné: <span className="bg-red-100 px-1 rounded">0g</span>
                                </div>
                              ) : donne > 0 ? (
                                <div>Donné: <b>{donne}g</b></div>
                              ) : null}
                              {utilise > 0 && <div className={anomalie ? 'text-red-600 font-semibold mt-1' : ''}>Utilisé: <b>{utilise}g</b></div>}
                              {restant > 0 && <div className="text-green-600">Restant: <b>{restant}g</b></div>}
                            </>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoriqueTaches;
